// Copyright (c) Mysten Labs, Inc.
// Modifications Copyright (c) 2024 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

import { AddressAlias, useCopyToClipboard, useGetObjectOrPastObject } from '@iota/core';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorBoundary, PageLayout, SyntaxHighlighter } from '~/components';
import { PageHeader } from '~/components/ui';
import { usePackageUpgradePolicy } from '~/hooks';
import { getHistoryUnavailableMessage, INDEXER_RETENTION_DAYS } from '~/lib/constants';
import { ObjectView } from '~/pages/object-result/views/ObjectView';
import { translate, type DataType } from './ObjectResultType';
import { PkgView, TokenView } from './views';
import {
    Badge,
    BadgeType,
    InfoBox,
    InfoBoxStyle,
    InfoBoxType,
    LoadingIndicator,
    Panel,
    Toggle,
    ToggleLabelPosition,
    ToggleSize,
} from '@iota/apps-ui-kit';
import { Warning } from '@iota/apps-ui-icons';

const PACKAGE_TYPE_NAME = 'Move Package';

export function ObjectResult(): JSX.Element {
    const { id: objID } = useParams();
    const { data, isPending, isError, isFetched } = useGetObjectOrPastObject(objID);
    const copyToClipboard = useCopyToClipboard();
    const [showRawJson, setShowRawJson] = useState(false);

    const rawJsonToggle = (
        <div className="flex w-full md:justify-end">
            <Toggle
                name="raw-json-toggle"
                label="Raw JSON"
                labelPosition={ToggleLabelPosition.Left}
                size={ToggleSize.Small}
                isToggled={showRawJson}
                onChange={setShowRawJson}
            />
        </div>
    );

    const isPageError = !isPending && (isError || data?.error || (isFetched && !data));
    const resp = data && !isPageError ? translate(data) : null;
    const isPackage = resp ? resp.objType === PACKAGE_TYPE_NAME : false;
    const txDigest = isPackage ? resp?.data.tx_digest : undefined;
    const { upgradePolicy } = usePackageUpgradePolicy(txDigest);

    if (isPending) {
        return (
            <PageLayout
                content={
                    <div className="flex w-full items-center justify-center">
                        <LoadingIndicator text="Loading data" />
                    </div>
                }
            />
        );
    }

    return (
        <PageLayout
            content={
                <div className="flex flex-col gap-y-2xl">
                    {!isPackage && !isPageError && (
                        <div className="flex flex-col gap-y-2xl">
                            <PageHeader
                                type="Object"
                                title={
                                    <div className="flex flex-col gap-xs">
                                        <AddressAlias
                                            address={resp?.id || ''}
                                            onCopy={() => copyToClipboard(resp?.id || '')}
                                        />
                                    </div>
                                }
                                showCopyButton={false}
                                after={data && rawJsonToggle}
                                error={
                                    data?.isViewingPastVersion
                                        ? 'This object was deleted. You are viewing a past version of this object.'
                                        : undefined
                                }
                            />
                            {!showRawJson && (
                                <ErrorBoundary>{data && <ObjectView data={data} />}</ErrorBoundary>
                            )}
                        </div>
                    )}
                    {data?.isHistoryUnavailable ? (
                        <InfoBox
                            title="Object No Longer Available"
                            supportingText={getHistoryUnavailableMessage('This object')}
                            icon={<Warning />}
                            type={InfoBoxType.Warning}
                            style={InfoBoxStyle.Elevated}
                        />
                    ) : isPageError || !data || !resp ? (
                        <InfoBox
                            title="Invalid Object ID"
                            supportingText={`No object found matching ID: ${objID} on this network. Please verify the ID and try again. Note that objects deleted more than ${INDEXER_RETENTION_DAYS} days ago can no longer be found.`}
                            icon={<Warning />}
                            type={InfoBoxType.Error}
                            style={InfoBoxStyle.Elevated}
                        />
                    ) : (
                        <>
                            {isPackage && (
                                <PageHeader
                                    type="Package"
                                    showCopyButton={false}
                                    after={rawJsonToggle}
                                    title={
                                        <AddressAlias
                                            address={resp.id}
                                            onCopy={() => copyToClipboard(resp.id)}
                                            renderAlias={(alias) => (
                                                <div className="flex items-center gap-xs">
                                                    <span>{alias}</span>
                                                    {upgradePolicy &&
                                                        !upgradePolicy.isIndeterminate && (
                                                            <span className="shrink-0">
                                                                <Badge
                                                                    label={
                                                                        upgradePolicy.isImmutable
                                                                            ? 'Immutable'
                                                                            : 'Upgradeable'
                                                                    }
                                                                    type={
                                                                        upgradePolicy.isImmutable
                                                                            ? BadgeType.Neutral
                                                                            : BadgeType.PrimarySoft
                                                                    }
                                                                />
                                                            </span>
                                                        )}
                                                </div>
                                            )}
                                        />
                                    }
                                />
                            )}
                            <ErrorBoundary>
                                {showRawJson ? (
                                    <Panel>
                                        <div className="p-md--rs">
                                            <SyntaxHighlighter
                                                code={JSON.stringify(data, null, 2)}
                                                language="json"
                                            />
                                        </div>
                                    </Panel>
                                ) : isPackage ? (
                                    <PkgView data={resp} />
                                ) : (
                                    <TokenView data={data} />
                                )}
                            </ErrorBoundary>
                        </>
                    )}
                </div>
            }
        />
    );
}

export type { DataType };
